import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import type { Resolver } from 'react-hook-form';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  type PriceTier,
  type PriceTierFormValues,
  priceTierFormSchema,
} from '@/schemas/commercial/price-table.schema';

/** Sentinel value for the last tier's "to" field (open-ended range, stored as null in DB) */
const OPEN_RANGE_TO = '__open__';

interface PriceTierInputProps {
  initialTiers?: PriceTier[];
  minQuantity?: number;
  onValidChange?: (tiers: PriceTier[] | undefined) => void;
  disabled?: boolean;
}

function mapTiersToFormValues(
  tiers: PriceTier[] | undefined,
  minQuantity: number,
): PriceTierFormValues['tiers'] {
  if (tiers && tiers.length > 0) {
    return tiers.map((tier, index) => {
      const isLast = index === tiers.length - 1;
      const toValue = isLast && tier.to === null ? OPEN_RANGE_TO : (tier.to?.toString() ?? '');

      return {
        from: tier.from.toString(),
        to: toValue,
        unitPrice: tier.unitPrice.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      };
    });
  }

  return [
    {
      from: minQuantity.toString(),
      to: '',
      unitPrice: '',
    },
    {
      from: minQuantity.toString(),
      to: OPEN_RANGE_TO,
      unitPrice: '',
    },
  ];
}

export function PriceTierInput({
  initialTiers,
  minQuantity = 1,
  onValidChange,
  disabled = false,
}: PriceTierInputProps) {
  const {
    control,
    register,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<PriceTierFormValues>({
    // Library type mismatch: zodResolver output type differs from Resolver<T> due to Zod v3 coercion
    resolver: zodResolver(priceTierFormSchema) as unknown as Resolver<PriceTierFormValues>,
    mode: 'onChange',
    defaultValues: {
      tiers: mapTiersToFormValues(initialTiers, minQuantity),
    },
  });

  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) {
      return;
    }

    reset({
      tiers: mapTiersToFormValues(initialTiers, minQuantity),
    });
    initializedRef.current = true;
  }, [initialTiers, minQuantity, reset]);

  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'tiers',
  });

  const watchedTiers = useWatch({
    control,
    name: 'tiers',
  });

  // Use ref to avoid re-render cascades from inline callback props
  const onValidChangeRef = useRef(onValidChange);
  onValidChangeRef.current = onValidChange;

  useEffect(() => {
    const parsed = priceTierFormSchema.safeParse({
      tiers: watchedTiers ?? [],
    });

    if (parsed.success) {
      onValidChangeRef.current?.(parsed.data.tiers);
    } else {
      onValidChangeRef.current?.(undefined);
    }
  }, [watchedTiers]);

  // Update the next tier's "from" when current tier's "to" changes (from = previous.to + 1)
  useEffect(() => {
    if (!watchedTiers || watchedTiers.length < 2) return;

    for (let i = 0; i < watchedTiers.length - 1; i++) {
      const currentTo = watchedTiers[i]?.to;
      if (!currentTo || currentTo === OPEN_RANGE_TO) continue;

      const currentToNum = Number.parseInt(currentTo, 10);
      if (Number.isNaN(currentToNum)) continue;

      const expectedNext = (currentToNum + 1).toString();
      const nextFrom = watchedTiers[i + 1]?.from;

      if (expectedNext !== nextFrom) {
        setValue(`tiers.${i + 1}.from`, expectedNext, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [watchedTiers, setValue]);

  const fieldsLength = fields.length;

  useEffect(() => {
    const lastIndex = fieldsLength - 1;
    if (lastIndex >= 0) {
      const current = getValues(`tiers.${lastIndex}.to`);
      if (current === '' || current === undefined || current === null) {
        setValue(`tiers.${lastIndex}.to`, OPEN_RANGE_TO, {
          shouldValidate: true,
        });
      }
    }
  }, [fieldsLength, getValues, setValue]);

  const handleAddTier = useCallback(() => {
    if (disabled) {
      return;
    }
    const currentTiers = getValues('tiers');
    const lastIndex = Math.max(currentTiers.length - 1, 0);

    // The last tier is always the open-ended one (to = OPEN_RANGE_TO).
    // Get the second-to-last tier to compute a reasonable "from" value.
    const secondToLast = currentTiers[lastIndex - 1];
    const prevTo = secondToLast?.to;
    const prevToNum = prevTo ? Number.parseInt(prevTo, 10) : 0;
    const nextFrom =
      Number.isNaN(prevToNum) || prevToNum === 0
        ? minQuantity.toString()
        : (prevToNum + 1).toString();

    insert(lastIndex, {
      from: nextFrom,
      to: '',
      unitPrice: '',
    });
  }, [disabled, getValues, insert, minQuantity]);

  const handleRemoveTier = useCallback(
    (index: number) => {
      if (disabled) {
        return;
      }
      remove(index);

      queueMicrotask(() => {
        const currentTiers = getValues('tiers');

        if (index > 0 && index < currentTiers.length) {
          const previousTo = currentTiers[index - 1]?.to ?? '';
          const prevToNum = Number.parseInt(previousTo, 10);
          const newFrom =
            !Number.isNaN(prevToNum) && previousTo !== OPEN_RANGE_TO
              ? (prevToNum + 1).toString()
              : previousTo;
          setValue(`tiers.${index}.from`, newFrom, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      });
    },
    [disabled, getValues, remove, setValue],
  );

  const rootError = (errors as any)?.root?.message as string | undefined;
  const tiersErrors = errors.tiers;

  const showDeleteColumn = fields.length > 2;

  return (
    <div>
      {/* Tier rows */}
      <div className="space-y-2">
        {fields.map((field, index) => {
          const isLast = index === fields.length - 1;
          const fromError =
            Array.isArray(tiersErrors) && tiersErrors[index] && 'from' in tiersErrors[index]
              ? (tiersErrors[index]?.from?.message as string | undefined)
              : undefined;

          const toError =
            Array.isArray(tiersErrors) && tiersErrors[index] && 'to' in tiersErrors[index]
              ? (tiersErrors[index]?.to?.message as string | undefined)
              : undefined;

          const unitPriceError =
            Array.isArray(tiersErrors) && tiersErrors[index] && 'unitPrice' in tiersErrors[index]
              ? (tiersErrors[index]?.unitPrice?.message as string | undefined)
              : undefined;

          const fromRegister = register(`tiers.${index}.from`);
          const toRegister = register(`tiers.${index}.to`);

          // Inline range validation: "Até" must be greater than "De"
          const fromVal = Number.parseInt(watchedTiers?.[index]?.from ?? '', 10);
          const toVal = Number.parseInt(watchedTiers?.[index]?.to ?? '', 10);
          const rangeError =
            !isLast && !Number.isNaN(fromVal) && !Number.isNaN(toVal) && toVal < fromVal
              ? '"Até" deve ser maior ou igual a "De"'
              : undefined;

          return (
            <div key={field.id}>
              {/* Per-row header */}
              <div
                className={`grid gap-3 mb-1 ${showDeleteColumn ? 'grid-cols-[1fr_1fr_1fr_2rem]' : 'grid-cols-3'}`}
              >
                {!isLast ? (
                  <>
                    <Label className="text-xs text-muted-foreground">De</Label>
                    <Label className="text-xs text-muted-foreground">Até</Label>
                  </>
                ) : (
                  <>
                    <span />
                    <Label className="text-xs text-muted-foreground">A partir de</Label>
                  </>
                )}
                <Label className="text-xs text-muted-foreground">Preço un.</Label>
                {showDeleteColumn && <span />}
              </div>

              <div
                className={`grid gap-3 items-start ${showDeleteColumn ? 'grid-cols-[1fr_1fr_1fr_2rem]' : 'grid-cols-3'}`}
              >
                <div>
                  {!isLast ? (
                    <>
                      <Input
                        {...fromRegister}
                        readOnly={index === 0}
                        aria-readonly={index === 0}
                        placeholder={index === 0 ? '1' : '0'}
                        disabled={disabled}
                        className="h-8 text-sm"
                      />
                      {fromError && <p className="text-xs text-red-500 mt-0.5">{fromError}</p>}
                    </>
                  ) : (
                    <span />
                  )}
                </div>
                <div>
                  {!isLast ? (
                    <>
                      <Input
                        {...toRegister}
                        placeholder="0"
                        disabled={disabled}
                        className="h-8 text-sm"
                      />
                      {(rangeError || toError) && (
                        <p className="text-xs text-red-500 mt-0.5">{rangeError || toError}</p>
                      )}
                    </>
                  ) : (
                    <Input
                      value={watchedTiers?.[index]?.from || ''}
                      readOnly
                      disabled
                      className="h-8 text-sm text-muted-foreground"
                    />
                  )}
                </div>
                <div>
                  <CurrencyInput
                    value={watchedTiers?.[index]?.unitPrice || '0,00'}
                    onChange={(v) =>
                      setValue(`tiers.${index}.unitPrice`, v, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    placeholder="0,00"
                    disabled={disabled}
                    error={unitPriceError}
                    className="h-8 text-sm"
                  />
                </div>
                {showDeleteColumn && (
                  <div className="flex items-center h-8">
                    {index > 0 && !isLast && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={disabled}
                        onClick={() => handleRemoveTier(index)}
                        className="h-7 w-7"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-1 pt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddTier}
          disabled={disabled}
        >
          Adicionar faixa
        </Button>
        {rootError && <p className="text-xs text-red-500">{rootError}</p>}
      </div>
    </div>
  );
}
